import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  fetchMeisaiIssueSettings,
  updateMeisaiIssueDefault,
  updateMeisaiIssueSetting,
  type MeisaiIssueSettingListParams,
  type MeisaiIssueType,
} from '@/services/main/meisaiIssueSetting';
import { useClinicContext } from './useClinicContext';

/**
 * 令和8年法改正 No 5 — 明細書発行設定 (治療院情報 tab).
 *
 * Lists patients with the setting in force this month next to the one booked for next
 * month. Settings only: nothing is printed and no existing visit is modified — the
 * setting decides how the checkbox on the visit form is pre-filled from here on.
 */
const QUERY_KEY = 'meisai-issue-settings';

export const useMeisaiIssueSettings = (params: MeisaiIssueSettingListParams, enabled = true) => {
  const { tenant, clinicNumber } = useClinicContext();

  return useQuery({
    queryKey: [QUERY_KEY, tenant, clinicNumber, params],
    queryFn: () => fetchMeisaiIssueSettings(tenant ?? '', clinicNumber ?? '', params),
    enabled: enabled && Boolean(tenant && clinicNumber),
  });
};

export const useUpdateMeisaiIssueSetting = () => {
  const { tenant, clinicNumber } = useClinicContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: {
      patient_id: number;
      apply_year_month: string;
      issue_type: MeisaiIssueType;
    }) => updateMeisaiIssueSetting(tenant ?? '', clinicNumber ?? '', payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QUERY_KEY] }),
  });
};

export const useUpdateMeisaiIssueDefault = () => {
  const { tenant, clinicNumber } = useClinicContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (issueType: MeisaiIssueType) =>
      updateMeisaiIssueDefault(tenant ?? '', clinicNumber ?? '', issueType),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QUERY_KEY] }),
  });
};
